import React, {useEffect, useState} from "react";
import {Outlet} from "react-router-dom";
import {
    AppBar,
    Avatar,
    Badge,
    Box,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Tooltip,
    Link as MuiLink,
    Typography,
} from "@mui/material";
import {getMe, renew} from "../service/auth.service";
import {useAuthStore} from "../store/auth.store";
import {useNavigate} from "react-router";
import {ModalWindow} from "./modal/ModalWindow";
import {LoginForm} from "./auth/LoginForm";
import {PasswordRecoveryForm} from "./auth/PasswordRecoveryForm";
import {RegisterForm} from "./auth/RegisterForm";
import {enqueueSnackbar} from "notistack";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import RealEstateAgentOutlinedIcon from '@mui/icons-material/RealEstateAgentOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import {User} from "../types/User";
import {parseQueryParams, removeQueryParam} from "../utils/UrlUtils";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined';
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import {SubscriptionModal} from "./proposal/SubscriptionModal";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {requireAttentionAsAdmin, requireAttentionAsUser} from "../service/ticket.service";
import {isNavItemVisible, MAIN_NAV_ITEMS, MainNavItem} from "./mainNav";

const NAV_ICONS: Record<string, React.ReactNode> = {
    "Home": <BarChartOutlinedIcon fontSize="medium"/>,
    "Listings": <RealEstateAgentOutlinedIcon fontSize="medium"/>,
    "Add Listing": <AddHomeWorkOutlinedIcon/>,
    "Profile": <AccountCircleOutlinedIcon fontSize="medium"/>,
    "Users": <GroupsOutlinedIcon fontSize="medium"/>,
    "Phone-Accounts": <ContactPhoneOutlinedIcon fontSize="medium"/>,
    "Support Tickets": <ContactSupportOutlinedIcon fontSize="medium"/>,
    "Daily Subscription": <NotificationsActiveOutlinedIcon fontSize="medium"/>,
    "Scheduler": <ScheduleOutlinedIcon fontSize="medium"/>,
    "Cities": <LocationCityOutlinedIcon fontSize="medium"/>,
};

const Layout: React.FC = () => {
    const authStore = useAuthStore();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const open = Boolean(anchorEl);
    const [showSubscribe, setShowSubscribe] = useState(false);

    const [loginFormLoading, setLoginFormLoading] = useState<boolean>(false);
    const [registerFormLoading, setRegisterFormLoading] = useState<boolean>(false);
    const [passwordRecoveryFormLoading, setPasswordRecoveryFormLoading] = useState<boolean>(false);

    const navContext = {jwt: authStore.jwt, can: authStore.can};

    useEffect(() => {
        const interval = setInterval(() => {
            if (authStore.jwt) {
                renew().then(response => {
                    authStore.setJwt(response.data.jwt)
                }).catch((e) => {
                    authStore.logout(navigate);
                })
            }
        }, 7 * 60 * 1000);

        return () => {
            clearInterval(interval)
        }
    });


    const {data: containNotifications} = useQuery({
        queryKey: ['notifications', 'support'],
        refetchOnWindowFocus: false,
        enabled: !!authStore.user,
        queryFn: () => {
            let promise = authStore.can('be_support')
                ? requireAttentionAsAdmin()
                : requireAttentionAsUser();
            return promise.then(response => {
                return response.status === 200;
            }).catch(error => {
                enqueueSnackbar({
                    message: "Failed to load support tickets notifications: " + error.message,
                    variant: "error"
                })
                return false;
            })
        },
        placeholderData: keepPreviousData
    });

    useEffect(() => {
        if (!authStore.user && authStore.jwt) {
            getMe().then(r => {
                if (r.data) {
                    authStore.setUser(r.data)
                } else {
                    authStore.setUser(undefined);
                    authStore.setJwt(undefined);
                }
            }).catch((e) => {
                enqueueSnackbar({variant: "error", message: "Failed to load current user details, " + e.message})
            });
        }
    }, [authStore, authStore.user, authStore.jwt]);


    useEffect(() => {
        if (authStore.jwt) {
            return
        }
        if (authStore.showLogin) {
            return
        }
        const params = parseQueryParams(document.location.toString())
        if (params.login !== undefined) {
            removeQueryParam("login");
            authStore.setShowLogin(true);
        }
    }, [authStore, authStore.user, authStore.jwt]);

    function prepareUserName(user: User) {
        let name:string;
        if (user.username) {
            name = user.username
        } else if (user.login) {
            const position = user.login.indexOf("@");
            name = position > -1 ? user.login.substring(0, position) : user.login;
        } else if(user.phone) {
            name = user.phone;
        } else {
            return null;
        }
        return <MuiLink href={"/profile"} onClick={(e) => {
            e.preventDefault();
            navigate("/profile")
        }}>{name}</MuiLink>
    }

    function closeDrawer() {
        setDrawerOpen(false);
    }

    function handleNavItem(item: MainNavItem) {
        closeDrawer();
        if (item.kind === "subscribe") {
            setShowSubscribe(true);
            return;
        }
        if (item.kind === "profile") {
            if (authStore.jwt) {
                navigate("/profile");
            } else {
                authStore.setShowLogin(true);
            }
            return;
        }
        if (item.to) {
            navigate(item.to);
        }
    }

    function handleAccountProfile() {
        setAnchorEl(null);
        if (authStore.jwt) {
            navigate("/profile");
        } else {
            authStore.setShowLogin(true);
        }
    }

    function handleAccountSession() {
        setAnchorEl(null);
        closeDrawer();
        if (authStore.jwt) {
            authStore.logout(navigate);
        } else {
            authStore.setShowLogin(true);
        }
    }

    const visibleNavItems = MAIN_NAV_ITEMS.filter((item) => isNavItemVisible(item, navContext));

    return (<Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
    }}>
        <AppBar position="static" color={"default"}>
            <Toolbar>
                <Tooltip title="Open navigation">
                    <IconButton
                        id="nav-drawer-button"
                        edge="start"
                        aria-label="open navigation"
                        aria-controls={drawerOpen ? "nav-drawer" : undefined}
                        aria-expanded={drawerOpen ? "true" : undefined}
                        onClick={() => setDrawerOpen(true)}
                        color="inherit"
                        sx={{mr: 1}}
                    >
                        <Badge
                            variant="dot"
                            color="error"
                            overlap="circular"
                            invisible={!containNotifications}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            <MenuIcon fontSize={"large"}/>
                        </Badge>
                    </IconButton>
                </Tooltip>
                <a
                    href="/"
                    onClick={(e) => {
                    e.preventDefault();
                    let isInIframe;
                    try {
                        isInIframe = window.self !== window.top;
                    } catch (e) {
                        isInIframe = true;
                    }
                    if (isInIframe) {
                        window.open("https://relapka.com/", '_blank')
                    } else {
                        navigate("/")
                    }
                }}
                    style={{display: "flex", alignItems: "center", lineHeight: 0}}
                >
                    <Avatar
                        alt="Logo"
                        src="/images/logo.png"
                        variant="rounded"
                        sx={{
                            width: {xs: 40, sm: 48},
                            height: {xs: 40, sm: 48},
                            mr: 1,
                        }}
                    />
                </a>
                <Typography sx={{display: {xs: 'none', md: 'block'}}}>
                    All ads for sale of secondary real estate in Batumi are collected and verified in one table.
                </Typography>
                <Box sx={{flexGrow: 1}}/>

                <Stack flexDirection={"row"} gap={"10px"} alignItems={"center"}>
                    {authStore.user ? prepareUserName(authStore.user) : null}

                    {!authStore.jwt && <IconButton onClick={() => authStore.setShowLogin(true)} aria-label="quick login">
                        <LoginOutlinedIcon fontSize="medium"/>
                    </IconButton>}


                    <Tooltip title="Account">
                            <IconButton
                                id="topbar-menu-icon-button"
                                edge="end"
                                aria-label="account menu"
                                aria-controls={open ? "topbar-menu" : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? "true" : undefined}
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                color="inherit"
                                sx={{ml: 1}}
                            >
                                <AccountCircleOutlinedIcon fontSize={"large"}/>
                            </IconButton>
                    </Tooltip>
                </Stack>

                <Menu
                    id="topbar-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    onClick={() => setAnchorEl(null)}
                    anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                    MenuListProps={{"aria-label": "account menu"}}
                >
                    <MenuItem onClick={handleAccountProfile}>
                        <ListItemIcon>
                            <AccountCircleOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Profile
                    </MenuItem>
                    {authStore.jwt && <MenuItem onClick={handleAccountSession}>
                        <ListItemIcon>
                            <LogoutOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Logout
                    </MenuItem>}
                    {!authStore.jwt && <MenuItem onClick={handleAccountSession}>
                        <ListItemIcon>
                            <LoginOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Login
                    </MenuItem>}
                </Menu>

            </Toolbar>
        </AppBar>
        <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={closeDrawer}
            PaperProps={{
                style: {width: "100%", height: "100%", maxWidth: "100%"},
                "data-testid": "nav-drawer-paper",
            } as React.ComponentProps<typeof Drawer>["PaperProps"]}
        >
            <Box
                id="nav-drawer"
                data-testid="nav-drawer"
                role="navigation"
                aria-label="Main navigation drawer"
                style={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}
            >
                <Box
                    component="header"
                    data-testid="nav-drawer-header"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: "divider",
                    }}
                >
                    <Typography variant="h6" component="h2">Menu</Typography>
                    <IconButton aria-label="Close navigation" onClick={closeDrawer}>
                        <CloseIcon/>
                    </IconButton>
                </Box>
                <List aria-label="main navigation" sx={{flex: 1, overflow: "auto"}}>
                    {visibleNavItems.map((item) => {
                        const label = item.label === "Support Tickets" ? (
                            <Badge
                                variant="dot"
                                color="error"
                                overlap="rectangular"
                                invisible={!containNotifications}
                                anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                }}
                            >
                                {item.label}
                            </Badge>
                        ) : item.label;
                        return (
                            <ListItemButton key={item.id} onClick={() => handleNavItem(item)}>
                                <ListItemIcon>
                                    {NAV_ICONS[item.label]}
                                </ListItemIcon>
                                <ListItemText primary={label}/>
                            </ListItemButton>
                        );
                    })}
                </List>
                <List aria-label="session">
                    {authStore.jwt ? (
                        <ListItemButton onClick={handleAccountSession}>
                            <ListItemIcon>
                                <LogoutOutlinedIcon fontSize="medium"/>
                            </ListItemIcon>
                            <ListItemText primary="Logout"/>
                        </ListItemButton>
                    ) : (
                        <ListItemButton onClick={handleAccountSession}>
                            <ListItemIcon>
                                <LoginOutlinedIcon fontSize="medium"/>
                            </ListItemIcon>
                            <ListItemText primary="Login"/>
                        </ListItemButton>
                    )}
                </List>
            </Box>
        </Drawer>
        <Box component="main" sx={{
                 flexGrow: 1,
                 padding: { xs: '10px', md: '26px' },
             }}>
            <Outlet/>
        </Box>
        <Box
            component="footer"
            sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                py: 2,
                px: { xs: 2, md: 4 },
                backgroundColor: 'background.paper',
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
                <Typography variant="body2" color="text.secondary">
                    © {new Date().getFullYear()} Relapka
                </Typography>

                <Stack direction="row" spacing={2}>
                    <MuiLink href="/terms-and-conditions" target={"_blank"}  rel="noopener noreferrer">Terms & Conditions</MuiLink>
                    <MuiLink href="/privacy-policy" target={"_blank"}  rel="noopener noreferrer">Privacy Policy</MuiLink>
                </Stack>
            </Stack>
        </Box>
        {authStore.showLogin && <ModalWindow loading={loginFormLoading} onClose={() => authStore.setShowLogin(false)}>
            <LoginForm setLoading={setLoginFormLoading}/>
        </ModalWindow>}
        {authStore.showRegister &&
            <ModalWindow loading={registerFormLoading} onClose={() => authStore.setShowRegister(false)}>
                <RegisterForm setLoading={setRegisterFormLoading}/>
            </ModalWindow>}
        {authStore.showResetPassword &&
            <ModalWindow loading={passwordRecoveryFormLoading} onClose={() => authStore.setShowResetPassword(false)}>
                <PasswordRecoveryForm setLoading={setPasswordRecoveryFormLoading}/>
            </ModalWindow>}

        {showSubscribe && <SubscriptionModal
            onClose={() => setShowSubscribe(false)}
        />}
    </Box>);
};

export default Layout;
